import { isExpression, isGroup } from "./tree.js";
import type { FilterExpression, FilterGroup, FilterTree } from "./types.js";

/**
 * The "standard layout" is the simplified shape the Windows filter editor can
 * render in its flow view: an outer `or` group whose items are `and` groups of
 * expressions. Anything else falls back to the advanced (DevExpress) editor
 * there, and to the nested tree editor on the web.
 *
 * @see `afFilterEditor.CanRenderFilter`
 */
export function isStandardLayout(filter: FilterGroup | FilterExpression | null | undefined): boolean {
	if (!filter || isExpression(filter) || filter.mode !== "or") return false;

	return filter.items.every((item) => isGroup(item) && item.mode === "and" && item.items.every(isExpression));
}

/**
 * Coerces an arbitrary filter tree into the standard layout.
 *
 * An `or` group's items each become their own `and` group. Anything else is
 * flattened into a single `and` group. This loses nesting, so only use it when
 * the consumer cannot handle a nested tree.
 */
export function coerceToStandardLayout(filter: FilterGroup | FilterExpression | null | undefined): FilterTree {
	if (!filter) return { type: "group", mode: "or", items: [{ type: "group", mode: "and", items: [] }] };

	if (isExpression(filter)) {
		return { type: "group", mode: "or", items: [{ type: "group", mode: "and", items: [filter] }] };
	}

	if (filter.mode === "or") {
		let groups = filter.items.map((item) => {
			let expressions: FilterExpression[] = [];
			collectExpressions(item, expressions);

			return { type: "group", mode: "and", items: expressions } satisfies FilterGroup;
		});

		return {
			type: "group",
			mode: "or",
			items: groups.length > 0 ? groups : [{ type: "group", mode: "and", items: [] }],
		};
	}

	let expressions: FilterExpression[] = [];
	collectExpressions(filter, expressions);

	return { type: "group", mode: "or", items: [{ type: "group", mode: "and", items: expressions }] };
}

/**
 * Collects every expression in a (possibly nested) filter, in document order.
 */
function collectExpressions(item: FilterGroup | FilterExpression, into: FilterExpression[]) {
	if (isExpression(item)) {
		into.push(item);
		return;
	}

	for (let child of item.items) {
		collectExpressions(child, into);
	}
}
